import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { formatDate, formatViews, getCategoryName } from '@/utils/content';

interface ContentCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'horizontal';
}

export default function ContentCard({ article, variant = 'default' }: ContentCardProps) {
  const categoryName = getCategoryName(article.category);

  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col gap-3 rounded-lg bg-background-50 p-3 transition-colors hover:bg-background-100"
      >
        <div className="w-full h-36 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} 鲁啊鲁资讯`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div>
          <span className="text-xs font-medium text-primary-700">{categoryName}</span>
          <h3 className="mt-1 text-sm font-bold leading-snug text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors">
            {article.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-500">
            {formatDate(article.date)} · {formatViews(article.views)} 阅读
          </p>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-4 rounded-lg bg-background-50 p-3 transition-colors hover:bg-background-100"
      >
        <div className="w-28 h-20 sm:w-36 sm:h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} 鲁啊鲁资讯`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-xs font-medium text-primary-700">{categoryName}</span>
          <h3 className="mt-1 text-sm sm:text-base font-bold leading-snug text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors">
            {article.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-500 hidden sm:block line-clamp-2">
            {article.summary}
          </p>
          <p className="mt-1.5 text-xs text-foreground-400">
            {article.author} · {formatDate(article.date)} · {formatViews(article.views)} 阅读
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col h-full rounded-lg overflow-hidden border border-background-200 bg-background-50 transition-all duration-300 hover:border-primary-300 hover:-translate-y-1"
    >
      <div className="w-full h-48 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title} 鲁啊鲁资讯`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-col flex-1 p-4">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
            {categoryName}
          </span>
          <span className="text-xs text-foreground-400">{formatDate(article.date)}</span>
        </div>
        <h3 className="mt-3 text-base font-bold leading-snug text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2 flex-1">
          {article.summary}
        </p>
        <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
          <span>{article.author}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}